import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { DocumentController } from '../controllers/document-controller.js';
import { REQUIREMENT_PRIORITIES, generationChanged } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// <sysml-requirements-panel> -- Requirements CRUD editor
//
// Ported from: SysmlRequirementsEditor.vue
//
// Features:
// - Tabbed view (requirements / parts / behaviors)
// - WYSIWYG requirement cards with inline editing
// - Inline edit mode for name and documentation
// - Add/delete functionality
// - Priority badges and kind badges
// - Click-to-navigate in editor
// ---------------------------------------------------------------------------

type ViewTab = 'requirements' | 'parts' | 'behaviors';

interface ModelElement {
  id: string;
  name: string;
  elementType: string;
  text?: string;
  source?: { startLine: number; startColumn: number; endLine: number; endColumn: number };
  [key: string]: unknown;
}

@customElement('sysml-requirements-panel')
export class SysmlRequirementsPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host { display: flex; flex-direction: column; height: 100%; }

      /* -- Tabs -- */
      .tabs {
        display: flex;
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .tab {
        padding: var(--r5-space-2) var(--r5-space-3);
        font-size: var(--r5-text-xs);
        font-weight: 500;
        color: var(--r5-text-muted);
        cursor: pointer;
        border-bottom: 2px solid transparent;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
        background: none;
        border-top: none;
        border-left: none;
        border-right: none;
      }
      .tab:hover { color: var(--r5-text); }
      .tab.active {
        color: var(--r5-primary);
        border-bottom-color: var(--r5-primary);
      }
      .tab-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        border-radius: 9px;
        background: var(--r5-surface-muted);
        font-size: var(--r5-font-size-badge);
        font-weight: 600;
        margin-left: var(--r5-space-1);
        padding: 0 4px;
      }
      .tab.active .tab-count {
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
        color: var(--r5-primary);
      }

      /* -- List -- */
      .list {
        padding: var(--r5-space-2);
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-2);
      }

      /* -- Item card -- */
      .item {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        padding: var(--r5-space-2) var(--r5-space-3);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
      }
      .item:hover {
        border-color: color-mix(in srgb, var(--r5-primary) 40%, var(--r5-border));
        background: color-mix(in srgb, var(--r5-primary) 2%, transparent);
      }
      .item-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--r5-space-2);
      }
      .item-name {
        font-weight: 600;
        font-size: var(--r5-text-xs);
        cursor: pointer;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .item-name:hover { color: var(--r5-primary); text-decoration: underline; text-decoration-style: dotted; }
      .item-badges {
        display: flex;
        gap: var(--r5-space-1);
        flex-shrink: 0;
      }
      .item-doc {
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        line-height: 1.4;
        max-height: 3em;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
      .item-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--r5-space-2);
        margin-top: var(--r5-space-1);
      }
      .item-source {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        font-family: var(--r5-font-mono);
      }
      .item-actions {
        display: flex;
        gap: var(--r5-space-1);
        opacity: 0;
        transition: opacity var(--r5-duration-fast) var(--r5-ease-default);
      }
      .item:hover .item-actions { opacity: 1; }

      /* -- Inline edit -- */
      .edit-overlay {
        padding: var(--r5-space-3);
        border: 2px solid var(--r5-primary);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        box-shadow: var(--r5-shadow-sm);
      }
      .edit-field {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        margin-bottom: var(--r5-space-2);
      }
      .edit-field label {
        font-size: var(--r5-text-xs);
        font-weight: 500;
        color: var(--r5-text-muted);
      }
      .edit-field input, .edit-field textarea {
        width: 100%;
        padding: var(--r5-space-2);
        font-family: var(--r5-font-sans);
        font-size: var(--r5-text-xs);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        color: var(--r5-text);
        box-sizing: border-box;
      }
      .edit-field textarea { min-height: 60px; resize: vertical; }
      .edit-field input:focus, .edit-field textarea:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }
      .edit-actions {
        display: flex;
        gap: var(--r5-space-2);
        justify-content: flex-end;
      }

      /* -- Add form -- */
      .add-form {
        padding: var(--r5-space-3);
        border-top: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .add-form input, .add-form textarea {
        width: 100%;
        padding: var(--r5-space-2);
        font-family: var(--r5-font-sans);
        font-size: var(--r5-text-xs);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        color: var(--r5-text);
        margin-bottom: var(--r5-space-2);
        box-sizing: border-box;
      }
      .add-form textarea { min-height: 60px; resize: vertical; }
      .add-form input:focus, .add-form textarea:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }
      .form-actions {
        display: flex;
        gap: var(--r5-space-2);
        justify-content: flex-end;
      }

      /* -- Kind badge colors -- */
      .kind-partDef { background: var(--r5-primary-50); color: var(--r5-primary); border: 1px solid var(--r5-primary-200); }
      .kind-requirementDef { background: var(--r5-success-50); color: var(--r5-success-700); border: 1px solid var(--r5-success-200); }
      .kind-actionDef { background: var(--r5-warning-50); color: var(--r5-warning-700); border: 1px solid var(--r5-warning-200); }
    `,
  ];

  // CRITICAL: hasChanged: generationChanged for controller properties
  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;
  @property({ attribute: false, hasChanged: generationChanged }) documents!: DocumentController;

  @state() private _activeTab: ViewTab = 'requirements';
  @state() private _showAddForm = false;
  @state() private _newName = '';
  @state() private _newDoc = '';
  @state() private _editingId: string | null = null;
  @state() private _editName = '';
  @state() private _editDoc = '';

  override render() {
    return html`
      <div class="panel-header">
        <h3>Model Elements</h3>
        <ark-button size="xs" variant="ghost" @click=${() => (this._showAddForm = !this._showAddForm)}>
          <ark-icon name="plus" size="xs"></ark-icon>
          Add
        </ark-button>
      </div>

      <div class="tabs">
        ${(['requirements', 'parts', 'behaviors'] as ViewTab[]).map(
          (tab) => html`
            <button
              class="tab ${this._activeTab === tab ? 'active' : ''}"
              @click=${() => (this._activeTab = tab)}
            >
              ${tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span class="tab-count">${this._getCount(tab)}</span>
            </button>
          `,
        )}
      </div>

      <div class="panel-body" style="padding: 0;">
        <div class="list">${this._renderList()}</div>
      </div>

      ${this._showAddForm ? this._renderAddForm() : nothing}
    `;
  }

  private _renderList() {
    const elements = this._getElements();

    if (elements.length === 0) {
      return html`
        <div class="empty-state">
          <ark-icon name="file-text" size="lg"></ark-icon>
          <div>No ${this._activeTab} found in the model.</div>
          <ark-button size="xs" variant="outline" @click=${() => (this._showAddForm = true)}>
            <ark-icon name="plus" size="xs"></ark-icon>
            Add ${this._activeTab === 'requirements' ? 'a requirement' : this._activeTab === 'parts' ? 'a part' : 'an action'}
          </ark-button>
        </div>
      `;
    }

    return elements.map((el) =>
      this._editingId === el.id ? this._renderEditCard(el) : this._renderItemCard(el),
    );
  }

  private _renderItemCard(el: ModelElement) {
    const kindClass =
      el.elementType === 'partDef' ? 'kind-partDef'
      : el.elementType === 'requirementDef' ? 'kind-requirementDef'
      : el.elementType === 'actionDef' || el.elementType === 'stateDef' ? 'kind-actionDef'
      : 'badge-neutral';

    const kindLabel =
      el.elementType === 'partDef' ? 'part'
      : el.elementType === 'requirementDef' ? 'req'
      : el.elementType === 'actionDef' ? 'action'
      : el.elementType === 'stateDef' ? 'state'
      : el.elementType;

    return html`
      <div class="item">
        <div class="item-header">
          <span class="item-name" @click=${() => this._onItemClick(el)} title="Go to source">
            ${el.name}
          </span>
          <div class="item-badges">
            <span class="badge ${kindClass}">${kindLabel}</span>
          </div>
        </div>
        ${el.text
          ? html`<div class="item-doc">${el.text}</div>`
          : nothing}
        <div class="item-footer">
          ${el.source
            ? html`<span class="item-source">L${el.source.startLine}</span>`
            : html`<span></span>`}
          <div class="item-actions">
            <ark-button size="xs" variant="ghost" @click=${() => this._startEdit(el)} title="Edit">
              <ark-icon name="pencil" size="xs"></ark-icon>
            </ark-button>
            <ark-button size="xs" variant="ghost" @click=${() => this._onDelete(el)} title="Delete">
              <ark-icon name="trash-2" size="xs"></ark-icon>
            </ark-button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderEditCard(el: ModelElement) {
    return html`
      <div class="edit-overlay">
        <div class="edit-field">
          <label>Name</label>
          <input
            type="text"
            .value=${this._editName}
            @input=${(e: InputEvent) => (this._editName = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="edit-field">
          <label>Documentation</label>
          <textarea
            .value=${this._editDoc}
            @input=${(e: InputEvent) => (this._editDoc = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>
        <div class="edit-actions">
          <ark-button size="xs" variant="ghost" @click=${() => (this._editingId = null)}>
            Cancel
          </ark-button>
          <ark-button size="xs" variant="primary" @click=${() => this._saveEdit(el)}>
            Update
          </ark-button>
        </div>
      </div>
    `;
  }

  private _renderAddForm() {
    const kindLabel =
      this._activeTab === 'requirements' ? 'requirement def'
      : this._activeTab === 'parts' ? 'part def'
      : 'action def';

    return html`
      <div class="add-form">
        <div style="font-size: var(--r5-text-xs); font-weight: 600; margin-bottom: var(--r5-space-2);">
          Add ${kindLabel}
        </div>
        <input
          type="text"
          placeholder="Name (e.g., ${this._activeTab === 'requirements' ? 'SafetyReq' : this._activeTab === 'parts' ? 'Sensor' : 'ProcessData'})"
          .value=${this._newName}
          @input=${(e: InputEvent) => (this._newName = (e.target as HTMLInputElement).value)}
        />
        <textarea
          placeholder="Documentation text..."
          .value=${this._newDoc}
          @input=${(e: InputEvent) => (this._newDoc = (e.target as HTMLTextAreaElement).value)}
        ></textarea>
        <div class="form-actions">
          <ark-button size="xs" variant="ghost" @click=${() => (this._showAddForm = false)}>
            Cancel
          </ark-button>
          <ark-button
            size="xs"
            variant="primary"
            ?disabled=${!this._newName.trim()}
            @click=${this._onAdd}
          >
            Insert ${kindLabel}
          </ark-button>
        </div>
      </div>
    `;
  }

  // -- Data access ---------------------------------------------------

  private _getElements(): ModelElement[] {
    const m = this.modeler;
    switch (this._activeTab) {
      case 'requirements':
        return m.requirements as unknown as ModelElement[];
      case 'parts':
        return m.partDefinitions as unknown as ModelElement[];
      case 'behaviors':
        return this._getBehaviors();
      default:
        return [];
    }
  }

  private _getBehaviors(): ModelElement[] {
    if (!this.modeler.parsedModel?.allElements) return [];
    const result: ModelElement[] = [];
    for (const el of this.modeler.parsedModel.allElements) {
      if (['actionDef', 'actionUsage', 'stateDef', 'stateUsage'].includes(el.elementType)) {
        result.push(el as unknown as ModelElement);
      }
    }
    return result;
  }

  private _getCount(tab: ViewTab): number {
    switch (tab) {
      case 'requirements': return this.modeler.requirements.length;
      case 'parts': return this.modeler.partDefinitions.length;
      case 'behaviors': return this._getBehaviors().length;
    }
  }

  // -- Handlers ------------------------------------------------------

  private _onItemClick(el: ModelElement): void {
    if (el.source) {
      this.modeler.revealInEditor(el.source);
    }
  }

  private _startEdit(el: ModelElement): void {
    this._editingId = el.id;
    this._editName = el.name;
    this._editDoc = (el.text as string) ?? '';
  }

  private _saveEdit(el: ModelElement): void {
    if (!this._editName.trim()) return;

    const keyword =
      el.elementType === 'requirementDef' ? 'requirement def'
      : el.elementType === 'partDef' ? 'part def'
      : el.elementType === 'actionDef' ? 'action def'
      : el.elementType === 'stateDef' ? 'state def'
      : 'part def';

    const newCode = `${keyword} ${this._editName.trim()} {\n${this._editDoc.trim() ? `  doc /* ${this._editDoc.trim()} */\n` : ''}}`;

    if (el.source) {
      // Replace the existing definition in-place
      this.modeler.replaceSourceRange(el.source, newCode);
    } else {
      // No source range — fall back to appending
      this.dispatchEvent(
        new CustomEvent('insert-code', {
          detail: { code: '\n' + newCode },
          bubbles: true,
          composed: true,
        }),
      );
    }

    this._editingId = null;
  }

  private _onDelete(el: ModelElement): void {
    if (el.source) {
      this.modeler.deleteSourceRange(el.source);
    }
  }

  private _onAdd(): void {
    if (!this._newName.trim()) return;

    let code = '';
    const name = this._newName.trim();
    const doc = this._newDoc.trim();

    switch (this._activeTab) {
      case 'requirements':
        code = `\nrequirement def ${name} {\n${doc ? `  doc /* ${doc} */\n` : ''}}`;
        break;
      case 'parts':
        code = `\npart def ${name} {\n${doc ? `  doc /* ${doc} */\n` : ''}}`;
        break;
      case 'behaviors':
        code = `\naction def ${name} {\n${doc ? `  doc /* ${doc} */\n` : ''}}`;
        break;
    }

    if (code) {
      this.dispatchEvent(
        new CustomEvent('insert-code', {
          detail: { code },
          bubbles: true,
          composed: true,
        }),
      );
    }

    this._newName = '';
    this._newDoc = '';
    this._showAddForm = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-requirements-panel': SysmlRequirementsPanel;
  }
}
